/**
 * AccountingExportDownload — QuickBooks Desktop / Transaction Pro CSV, on demand.
 *
 * ClickUp 86bc3awax stage 2.
 *
 * WHY IT LIVES HERE AND NOT ON SETUP & CONFIGURATION
 * -------------------------------------------------
 * The download already existed, on the Accounting Export card in web-dispatch's
 * Setup & Configuration. That card loads tenant settings first and returns early
 * if the load fails:
 *
 *     if (loadError !== null) return <p>{loadError}</p>;
 *
 * Those settings are gated on MANAGE_TENANT, which only PLATFORM_ADMIN and
 * TENANT_ADMIN hold. BILLING_MANAGER and BILLING_STAFF -- the people who actually
 * run the Transaction Pro import -- got a 403, the early return fired, and the
 * download button was never drawn. The export ROUTE is gated on VIEW_INVOICES,
 * which both roles do hold, so they were allowed the file and simply could not
 * reach the button.
 *
 * THE ONE RULE THIS COMPONENT FOLLOWS
 * -----------------------------------
 * It reads NO settings. It renders unconditionally and calls the export endpoint
 * directly. Gating a download behind a settings fetch is precisely the bug above,
 * and re-introducing any such dependency here would recreate it at a new address.
 * The tenant's line mode and export target are resolved SERVER-side inside the
 * route, which is where they belong -- this screen needs a date range and nothing
 * more.
 *
 * Permission: none client-side beyond being signed in. VIEW_INVOICES is enforced
 * by the API (three-layer security); a role without it gets a 403 and the message
 * below, rather than a hidden control that leaves the user guessing.
 */

import React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';

/** Local calendar day, never UTC (DEC-004 / DEC-066). */
function todayLocalIso(): string {
  const d = new Date();
  return [
    d.getFullYear().toString().padStart(4, '0'),
    (d.getMonth() + 1).toString().padStart(2, '0'),
    d.getDate().toString().padStart(2, '0'),
  ].join('-');
}

function monthStartLocalIso(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, '0')}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-01`;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: 'var(--color-text-secondary)',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

export function AccountingExportDownload(): React.JSX.Element {
  const opts = useApiOpts();
  const [from, setFrom] = React.useState(monthStartLocalIso());
  const [to, setTo] = React.useState(todayLocalIso());
  const [busy, setBusy] = React.useState(false);

  const rangeInvalid = from > to;

  const download = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await authedFetch(opts, `/v1/billing/accounting-export?from=${from}&to=${to}`);
      if (!res.ok) {
        // 403 is the informative case: the account lacks VIEW_INVOICES. Saying so
        // beats a bare status code, because the fix is a role change by an admin,
        // not something the user can retry their way out of.
        toast.error(
          res.status === 403
            ? 'Your account does not have permission to export invoices. Ask a tenant admin for the Billing role.'
            : `Export failed (${String(res.status)}).`,
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lodex-qbd-export-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      style={{
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        padding: '16px',
        marginBottom: '20px',
      }}
    >
      <h2 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>QuickBooks export</h2>
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          marginBottom: '12px',
        }}
      >
        Download a CSV for Transaction Pro Importer. Every line of an invoice repeats the same
        invoice number, so each invoice imports as one invoice rather than one per line.
      </p>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle} htmlFor="qbd-from">
            From
          </label>
          <input
            id="qbd-from"
            type="date"
            value={from}
            onChange={(e): void => setFrom(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="qbd-to">
            To
          </label>
          <input
            id="qbd-to"
            type="date"
            value={to}
            onChange={(e): void => setTo(e.target.value)}
            style={inputStyle}
          />
        </div>
        <Button
          type="button"
          disabled={busy || rangeInvalid}
          onClick={(): void => {
            void download();
          }}
        >
          {busy ? 'Preparing…' : 'Download CSV'}
        </Button>
      </div>

      {rangeInvalid && (
        <p
          role="alert"
          style={{ fontSize: '12px', color: 'var(--color-danger)', marginTop: '8px' }}
        >
          The start date is after the end date.
        </p>
      )}
    </section>
  );
}
